// Custom hook for artist management
import { useState, useEffect } from 'react';
import { artistService } from '../../backend/services/artists';
import { toast } from 'sonner';
import type { 
  Artist, 
  CreateArtistData, 
  UpdateArtistData, 
  ArtistsFilter,
  ArtistStats
} from '../../shared/types/entities';

export const useArtists = (filter: ArtistsFilter = {}) => {
  const [artists, setArtists] = useState<Artist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchArtists = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await artistService.getArtists(filter);
      setArtists(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch artists');
    } finally {
      setLoading(false);
    }
  };

  const createArtist = async (artistData: CreateArtistData): Promise<Artist> => {
    if (import.meta.env.DEV) {
      console.log('--- Submitting to API ---');
      console.log('createArtist payload:', artistData);
      console.log('Does it have lastfm_data?', {}.hasOwnProperty.call(artistData, 'lastfm_data'));
    }
    try {
      setError(null);
      const newArtist = await artistService.createArtist(artistData);
      setArtists(prev => [newArtist, ...prev]);
      return newArtist;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create artist';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const updateArtist = async (id: string, updates: UpdateArtistData): Promise<Artist> => {
    try {
      setError(null);
      const updatedArtist = await artistService.updateArtist(id, updates);
      setArtists(prev => prev.map(a => a.id === id ? updatedArtist : a));
      return updatedArtist;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update artist';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const archiveArtist = async (id: string): Promise<void> => {
    try {
      setError(null);
      toast('Archiving artist...', { duration: 1000 });
      await artistService.archiveArtist(id);
      setArtists(prev => prev.filter(a => a.id !== id));
      toast.success('Artist archived successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to archive artist';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const restoreArtist = async (id: string): Promise<void> => {
    try {
      setError(null);
      toast('Restoring artist...', { duration: 1000 });
      await artistService.restoreArtist(id);
      setArtists(prev => prev.filter(a => a.id !== id));
      toast.success('Artist restored successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restore artist';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const getArchivedArtists = async (): Promise<Artist[]> => {
    try {
      setError(null);
      const data = await artistService.getArchivedArtists();
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch archived artists';
      setError(errorMessage);
      toast.error(errorMessage);
      return [];
    }
  };

  const deleteArtist = async (id: string): Promise<void> => {
    try {
      setError(null);
      toast('Deleting artist...', { duration: 1000 });
      await artistService.deleteArtist(id);
      setArtists(prev => prev.filter(a => a.id !== id));
      toast.success('Artist deleted successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete artist';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    fetchArtists();
  }, [JSON.stringify(filter)]);

  return {
    artists,
    loading,
    error,
    createArtist,
    updateArtist,
    archiveArtist,
    restoreArtist,
    deleteArtist,
    getArchivedArtists,
    refetch: fetchArtists
  };
};

// Hook for single artist
export const useArtist = (id: string | undefined) => {
  const [artist, setArtist] = useState<Artist | null>(null);
  const [stats, setStats] = useState<ArtistStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchArtist = async () => {
    if (!id) {
      setArtist(null);
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [artistData, statsData] = await Promise.all([
        artistService.getArtistById(id),
        artistService.getArtistStats(id)
      ]);
      
      setArtist(artistData);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch artist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtist();
  }, [id]);

  return {
    artist,
    stats,
    loading,
    error,
    refetch: fetchArtist
  };
};

// Hook for genres
export const useGenres = () => {
  const [genres, setGenres] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGenres = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await artistService.getGenres();
      setGenres(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch genres');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGenres();
  }, []);

  return {
    genres,
    loading,
    error,
    refetch: fetchGenres
  };
};